import { decrypt } from "./cryptoUtil";

const STORAGE_KEY = "dmqData";
const CURRENT_STEP_KEY = "dmqCurrentStep";
const CURRENT_QUESTION_SET_ID = "questionSetId";
const IS_USER_NRI = "isUserNRI";
const CURRENT_INTERNAL_STEP_KEY = "dmqCurrentInternalStep";
const MEMBER_INFO_KEY = "memberInformation";
const EDIT_INFORMATION_KEY = "isEditInformation";
const SUBMISSION_COMPLETE_KEY = "isSubmissionComplete";
const QUESTION_SET_KEY = "questionSet";
const PDF_KEY = "isPDFGenerated";
const STEP_CHANGE_EVENT = "dmq-step-change";
export const DMQ_STEP_FLOW = "DMQ_STEP_FLOW";
const DMQ_MEDICAL_CONDITIONS = "DMQ_MEDICAL_CONDITIONS";

export const getStoredFormData = () => {
  if (typeof window === "undefined") return {}; //  SSR safety
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.log("Error reading from localStorage:", error);
    return {};
  }
};

export const saveStepData = (objectName, values) => {
  if (typeof window === "undefined") return; //  SSR safety

  try {
    const currentData = getStoredFormData() || {};

    // If objectName is passed, use it as the key instead of step
    const key = objectName;

    const updatedData = {
      ...currentData,
      [key]: values, // Store under objectName or step
    };

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
  } catch (error) {
    console.log("Error saving to localStorage:", error);
  }
};

export const clearFormData = () => {
  if (typeof window === "undefined") return; //  SSR safety
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.log("Error clearing localStorage:", error);
  }
};

export const setUserNRIDetails = (mode) => {
  if (typeof window === "undefined") return;
  try {
    // Always stringify to store as a string
    window.localStorage.setItem(IS_USER_NRI, JSON.stringify(mode));
  } catch (error) {
    console.log("Error setting nri mode:", error);
  }
};

export const getUserNRIDetails = () => {
  if (typeof window === "undefined") return false;
  try {
    const mode = window.localStorage.getItem(IS_USER_NRI);
    // Parse string back to boolean (or any type)
    return mode ? JSON.parse(mode) : false;
  } catch (error) {
    console.log("Error getting edit mode:", error);
    return false;
  }
};

export const getCurrentQuestionSetId = () => {
  if (typeof window === "undefined") return null;
  try {
    const id = window.localStorage.getItem(CURRENT_QUESTION_SET_ID);
    return id ? id : null;
  } catch (error) {
    console.log("Error reading current question set id:", error);
    return null;
  }
};

export const saveQuestionSetId = (id) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CURRENT_QUESTION_SET_ID, id.toString());
    window.dispatchEvent(new Event("storage"));
  } catch (error) {
    console.log("Error saving current question set id:", error);
  }
};

export const clearQuestionSetId = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CURRENT_QUESTION_SET_ID);
  } catch (error) {
    console.log("Error clearing current question set id:", error);
  }
};

export const saveCurrentStep = (step) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CURRENT_STEP_KEY, step.toString());
    window.dispatchEvent(new Event("storage"));
  } catch (error) {
    console.log("Error saving current step:", error);
  }
};

export const getCurrentStep = () => {
  if (typeof window === "undefined") return null;
  try {
    const step = window.localStorage.getItem(CURRENT_STEP_KEY);
    return step ? step : null;
  } catch (error) {
    console.log("Error reading current step:", error);
    return null;
  }
};

export const clearCurrentStep = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CURRENT_STEP_KEY);
  } catch (error) {
    console.log("Error clearing current step:", error);
  }
};

export const saveCurrentInternalStep = (step) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(CURRENT_INTERNAL_STEP_KEY, step.toString());

    window.dispatchEvent(new CustomEvent(STEP_CHANGE_EVENT, { detail: { internalStep: step } }));
  } catch (error) {
    console.log("Error saving current step:", error);
  }
};

export const getCurrentInternalStep = () => {
  if (typeof window === "undefined") return null;
  try {
    const step = window.localStorage.getItem(CURRENT_INTERNAL_STEP_KEY);
    return step ? parseInt(step, 10) : null;
  } catch (error) {
    console.log("Error reading current step:", error);
    return null;
  }
};

export const clearCurrentInternalStep = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CURRENT_INTERNAL_STEP_KEY);
  } catch (error) {
    console.log("Error clearing current step:", error);
  }
};

export function setUser(user) {
  if (typeof window !== "undefined") {
    localStorage.setItem(MEMBER_INFO_KEY, JSON.stringify(user));
  }
}

export const getUser = async () => {
  if (typeof window === "undefined") return;
  try {
    const storedUser = JSON.parse(localStorage.getItem(MEMBER_INFO_KEY));
    if (storedUser) {
      const decryptedString = await decrypt(storedUser);

      const params = new URLSearchParams(decryptedString);
      const userObj = {};
      for (let [key, value] of params.entries()) {
        userObj[key] = decodeURIComponent(value.replace(/\+/g, " "));
      }

      return userObj;
    } else {
      return null;
    }
  } catch (error) {
    console.log("Error in fetching user information", error);
    return null;
  }
};

export function getUserToken() {
  if (typeof window === "undefined") return;
  try {
    const token = localStorage.getItem(MEMBER_INFO_KEY);
    return token ? JSON.parse(token) : null;
  } catch (error) {
    console.log("Error in fetching user token", error);
  }
}

export function clearUser() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(MEMBER_INFO_KEY);
  }
}

export const setEditingMode = (mode) => {
  if (typeof window === "undefined") return;
  try {
    // Always stringify to store as a string
    window.localStorage.setItem(EDIT_INFORMATION_KEY, JSON.stringify(mode));
  } catch (error) {
    console.log("Error setting edit mode:", error);
  }
};

export const getEditingMode = () => {
  if (typeof window === "undefined") return false;
  try {
    const mode = window.localStorage.getItem(EDIT_INFORMATION_KEY);
    // Parse string back to boolean (or any type)
    return mode ? JSON.parse(mode) : false;
  } catch (error) {
    console.log("Error getting edit mode:", error);
    return false;
  }
};

export const setSubmissionComplete = (mode) => {
  if (typeof window === "undefined") return;
  try {
    // Always stringify to store as a string
    window.localStorage.setItem(SUBMISSION_COMPLETE_KEY, JSON.stringify(mode));
  } catch (error) {
    console.log("Error setting submission Complete mode:", error);
  }
};

export const getSubmissionComplete = () => {
  if (typeof window === "undefined") return false;
  try {
    const mode = window.localStorage.getItem(SUBMISSION_COMPLETE_KEY);
    // Parse string back to boolean (or any type)
    return mode ? JSON.parse(mode) : false;
  } catch (error) {
    console.log("Error getting submission Complete:", error);
    return false;
  }
};

export const setQuestionBank = (set) => {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(QUESTION_SET_KEY, JSON.stringify(set));
  } catch (error) {
    console.log("Error in setting the question set", error);
  }
};

export const getQuestionBank = () => {
  if (typeof window === "undefined") return false;
  try {
    const questionSet = window.localStorage.getItem(QUESTION_SET_KEY);
    // Parse string back to boolean (or any type)
    return questionSet ? JSON.parse(questionSet) : {};
  } catch (error) {
    console.log("Error getting question set:", error);
    return false;
  }
};

export const clearQuestionBank = () => {
  if (typeof window === "undefined") return; //  SSR safety
  try {
    window.localStorage.removeItem(QUESTION_SET_KEY);
  } catch (error) {
    console.log("Error clearing question bank:", error);
  }
};

export const setPDFPresent = (mode) => {
  if (typeof window === "undefined") return;
  try {
    // Always stringify to store as a string
    window.localStorage.setItem(PDF_KEY, JSON.stringify(mode));
  } catch (error) {
    console.log("Error setting pdf:", error);
  }
};

export const getPDFPresent = () => {
  if (typeof window === "undefined") return false;
  try {
    const mode = window.localStorage.getItem(PDF_KEY);
    // Parse string back to boolean (or any type)
    return mode ? JSON.parse(mode) : false;
  } catch (error) {
    console.log("Error getting pdf mode:", error);
    return false;
  }
};

export const clearDMQStepFlow = () => {
  if (typeof window === "undefined") return; //  SSR safety
  try {
    window.localStorage.removeItem(DMQ_STEP_FLOW);
  } catch (error) {
    console.log("Error clearing step flow:", error);
  }
};

export const saveDMQStepFlow = (stepFlow) => {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(DMQ_STEP_FLOW, JSON.stringify(stepFlow));
  } catch (error) {
    console.log("Error in setting the step flow", error);
  }
};

export const getDMQStepFlow = () => {
  if (typeof window === "undefined") return false;
  try {
    const stepFlow = window.localStorage.getItem(DMQ_STEP_FLOW);
    // Parse string back to boolean (or any type)
    return stepFlow ? JSON.parse(stepFlow) : [];
  } catch (error) {
    console.log("Error getting step flow:", error);
    return false;
  }
};

export const saveDMQMedicalConditions = (conditions) => {
  if (typeof window === "undefined") return false;

  try {
    // Get existing conditions
    const existingConditions = JSON.parse(window.localStorage.getItem(DMQ_MEDICAL_CONDITIONS)) || {};

    // Merge old + new
    const updatedConditions = {
      ...existingConditions,
      ...conditions,
    };

    // Save merged object
    window.localStorage.setItem(DMQ_MEDICAL_CONDITIONS, JSON.stringify(updatedConditions));

    return updatedConditions;
  } catch (error) {
    console.log("Error in setting the medical conditions", error);
  }
};

export const getDMQMedicalConditions = () => {
  if (typeof window === "undefined") return false;
  try {
    const conditions = window.localStorage.getItem(DMQ_MEDICAL_CONDITIONS);
    // Parse string back to boolean (or any type)
    return conditions ? JSON.parse(conditions) : {};
  } catch (error) {
    console.log("Error getting medical conditions:", error);
    return false;
  }
};

export const clearDMQMedicalConditions = () => {
  if (typeof window === "undefined") return; //  SSR safety
  try {
    window.localStorage.removeItem(DMQ_MEDICAL_CONDITIONS);
  } catch (error) {
    console.log("Error clearing medical conditions:", error);
  }
};
